import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { fileUrl } from "@/lib/api";
import ShareSheet from "@/components/ShareSheet";
import { APP_LABELS, STATUS_META, gstSuffix } from "@/lib/constants";
import { toast } from "sonner";
import { Pencil, ImageIcon, FileText, FileDown, Image as ImageIco, MessageCircle, ChevronLeft, ChevronRight, Loader2, FolderOpen, ZoomIn, ZoomOut, X, Maximize2 } from "lucide-react";

function slug(s) {
  return (s || "tea").replace(/[^a-z0-9 _-]/gi, "").trim().replace(/\s+/g, "_") || "tea";
}

// Full-screen report viewer: fit-to-screen, tap to see native pixels (scroll to pan), pinch-zoom works natively
function ReportLightbox({ src, title, open, onOpenChange }) {
  const [zoomed, setZoomed] = useState(false);
  const close = () => { setZoomed(false); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-none w-screen h-[100dvh] p-0 border-0 bg-black/95 rounded-none overflow-hidden [&>button]:hidden" data-testid="report-lightbox">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setZoomed((z) => !z)} data-testid="report-lightbox-zoom" className="gap-1">
            {zoomed ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />} {zoomed ? "Fit to screen" : "Actual size"}
          </Button>
          <Button size="sm" variant="secondary" onClick={close} data-testid="report-lightbox-close"><X className="h-4 w-4" /></Button>
        </div>
        <div className={`absolute inset-0 ${zoomed ? "overflow-auto" : "overflow-hidden flex items-center justify-center"}`} onClick={() => setZoomed((z) => !z)}>
          <img
            src={src}
            alt="tasting report"
            className={zoomed ? "max-w-none cursor-zoom-out" : "max-w-full max-h-full object-contain cursor-zoom-in"}
            data-testid="report-lightbox-image"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductView({ product: p, brand, open, onOpenChange, onEdit, onPrev, onNext, hasPrev, hasNext }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  if (!p) return null;
  const teaName = p.brand_group || p.garden || p.name || "Tea sample";
  const licence = brand?.licence_number || "TB|LC|DI-10237";

  const apps = (p.best_application || []).map((a) => APP_LABELS[a] || a);
  const details = [
    ["Manufacturer / Supplier", p.garden],
    ["Invoice / Lot", [p.invoice_no, p.lot_no].filter(Boolean).join(" / ")],
    ["Cup / Liquor", p.liquor_color],
    ["Body & Strength", [p.body, p.strength].filter(Boolean).join(" · ")],
    ["Briskness", p.briskness],
    ["Rate / kg", p.price_per_kg ? `₹${p.price_per_kg}${gstSuffix(p)}` : null],
    ["Available", p.available_qty],
  ].filter(([, v]) => v);

  const download = async (kind) => {
    setBusy(kind);
    const map = {
      pdf: { url: `/products/${p.id}/export/pdf?quality=whatsapp`, ext: "pdf" },
      jpg: { url: `/products/${p.id}/export/jpg`, ext: "jpg" },
    }[kind];
    try {
      const res = await api.get(map.url, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${slug(teaName)}.${map.ext}`;
      a.click();
      URL.revokeObjectURL(u);
      toast.success(`${map.ext.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy("");
    }
  };

  const shareMsg = `Prime Gold Tea — ${teaName}${p.grade ? ` (${p.grade})` : ""}. Please review this tasting sheet and let us know if you would like a sample.`;
  const prepareShareJpg = async () => {
    const res = await api.get(`/products/${p.id}/export/jpg`, { responseType: "blob" });
    return new File([res.data], `${slug(teaName)}.jpg`, { type: "image/jpeg" });
  };
  const share = () => setShareOpen(true);

  const loadUsage = async () => {
    setUsageLoading(true);
    try {
      const { data } = await api.get(`/products/${p.id}/usage`);
      setUsage(data);
    } catch (e) {
      setUsage([]);
    } finally {
      setUsageLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="product-view">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={onPrev}
              disabled={!hasPrev}
              data-testid="view-prev"
              title="Previous tea"
              className="h-8 w-8 shrink-0 rounded-full"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="font-display text-2xl text-[#0B3B2E] flex-1 truncate">{teaName}</DialogTitle>
            <Button
              variant="outline"
              size="icon"
              onClick={onNext}
              disabled={!hasNext}
              data-testid="view-next"
              title="Next tea"
              className="h-8 w-8 shrink-0 rounded-full"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="rounded-lg border border-[#C9A34A]/40 bg-[#1D4837] px-4 py-3 flex items-center gap-4" data-testid="view-authenticity">
          <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-16 w-16 shrink-0 object-contain" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="inline-block rounded-md bg-[#C9A34A] text-[#0B3B2E] text-[10.5px] font-bold tracking-wide px-2.5 py-0.5">AUTHORISED DISTRIBUTOR BY TEA BOARD OF INDIA</div>
              <div className="flex items-center gap-1.5">
                <img src="/teaboard-seal.png" alt="Tea Board Authorised" className="h-5 w-5 object-contain" />
                <span className="text-[#F7F2E7] text-[12px] font-semibold" data-testid="view-licence">Licence no. {licence}</span>
              </div>
            </div>
            <div className="mt-2 rounded-md border border-[#C9A34A]/50 bg-[#C9A34A]/10 px-2.5 py-1.5">
              <p className="text-[#F3E6BE] text-[11px] italic leading-snug">This tea liquor image is completely natural and free from digital optimization. It shows the authentic color and clarity captured live during the photo session.</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-3">
          <div className="font-display text-3xl font-bold text-[#0B3B2E]" data-testid="view-garden-name">{teaName}</div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 -mt-1">
          {p.grade && <Badge variant="outline" className="bg-[#0B3B2E] text-[#C9A34A] border-[#0B3B2E] text-[11px]">{p.grade}</Badge>}
          {p.origin && <Badge variant="outline" className="bg-[#C9A34A]/15 text-[#0B3B2E] border-[#C9A34A]/40 text-[11px]">{p.origin}</Badge>}
          {p.tea_category && <Badge variant="outline" className="bg-[#14513D]/10 text-[#14513D] border-[#14513D]/30 text-[11px]">{p.tea_category}</Badge>}
          {p.stock_status === "out_of_stock" && <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[11px]">Out of stock</Badge>}
          <Popover onOpenChange={(o) => o && loadUsage()}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="view-usage-badge"
                className="inline-flex items-center rounded-full border border-[#14513D]/25 bg-[#0B3B2E]/5 text-[#14513D] text-[11px] px-2 py-0.5 font-medium hover:bg-[#0B3B2E]/10 transition-colors cursor-pointer"
              >
                Added {p.times_added || 0}×
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0" data-testid="view-usage-popover">
              <div className="px-3 py-2 border-b border-[#C9A34A]/20 bg-[#FAF7F0] flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-[#C9A34A]" />
                <span className="text-sm font-semibold text-[#0B3B2E]">In these catalogues</span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {usageLoading ? (
                  <div className="py-6 grid place-items-center text-[#14513D]/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : (usage && usage.length > 0) ? (
                  <div className="divide-y divide-[#C9A34A]/10">
                    {usage.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`view-usage-row-${c.id}`}
                        onClick={() => { onOpenChange(false); navigate(`/catalogue/${c.id}`); }}
                        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#C9A34A]/10 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#0B3B2E] truncate">{c.customer_name || c.title || c.catalogue_number}</div>
                          <div className="text-xs text-[#14513D]/60 truncate">
                            <span className="font-mono-id">{c.catalogue_number}</span> · {STATUS_META[c.status]?.label || c.status}
                          </div>
                        </div>
                        {c.count > 1 && <Badge variant="outline" className="bg-[#C9A34A]/15 text-[#0B3B2E] border-[#C9A34A]/40 text-[10px] shrink-0">×{c.count}</Badge>}
                        <ChevronRight className="h-4 w-4 text-[#C9A34A] shrink-0" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-[#14513D]/60" data-testid="view-usage-empty">Not used in any catalogue yet.</div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Export & share toolbar */}
        <div className="flex flex-wrap gap-2 mt-1">
          <Button size="sm" variant="outline" onClick={() => download("pdf")} disabled={busy === "pdf"} data-testid="view-download-pdf" className="gap-1 border-[#0B3B2E]/30 text-[#0B3B2E]">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => download("jpg")} disabled={busy === "jpg"} data-testid="view-download-jpg" className="gap-1 border-[#0B3B2E]/30 text-[#0B3B2E]">
            {busy === "jpg" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIco className="h-4 w-4" />} JPEG
          </Button>
          <Button size="sm" onClick={share} disabled={busy === "share"} data-testid="view-share-whatsapp" className="gap-1 bg-[#25D366] hover:bg-[#1FB855] text-white">
            {busy === "share" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} Share to WhatsApp
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#C9A34A] mb-1.5">Tasting (liquor) image</div>
            <div className="rounded-lg border border-[#C9A34A]/40 bg-white aspect-square grid place-items-center overflow-hidden">
              {p.liquor_image_url ? (
                <img src={fileUrl(p.liquor_image_url)} alt="liquor" className="w-full h-full object-contain" data-testid="view-liquor-image" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#14513D]/40"><ImageIcon className="h-8 w-8" /><span className="text-xs">No liquor image</span></div>
              )}
            </div>
          </div>
          <div className="space-y-2.5">
            {details.map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#C9A34A]">{k}</div>
                <div className="text-sm text-[#17251F]">{v}</div>
              </div>
            ))}
            {apps.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#C9A34A]">Best application</div>
                <div className="text-sm text-[#17251F]">{apps.join(", ")}</div>
              </div>
            )}
            {p.selling_note && <div className="rounded-md border-l-4 border-[#C9A34A] bg-[#F6E3A1]/70 px-3 py-2 font-display italic text-[#0B3B2E]" data-testid="view-selling-note">“{p.selling_note}”</div>}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#C9A34A]">Complete tea tasting report</div>
            {p.report_url && p.report_content_type !== "application/pdf" && (
              <button type="button" onClick={() => setLightbox(true)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#14513D] hover:text-[#0B3B2E]" data-testid="view-report-fullscreen">
                <Maximize2 className="h-3.5 w-3.5" /> Tap to view full screen
              </button>
            )}
          </div>
          <div className="flex justify-center">
            {p.report_url ? (
              p.report_content_type === "application/pdf" ? (
                <iframe title="tasting report" src={fileUrl(p.report_url)} className="w-full rounded-lg border border-[#C9A34A]/40 bg-white" style={{ height: 620 }} data-testid="view-report" />
              ) : (
                <img
                  src={fileUrl(p.report_url)}
                  alt="tasting report"
                  onClick={() => setLightbox(true)}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg border border-[#C9A34A]/40 bg-white p-1 cursor-zoom-in"
                  data-testid="view-report"
                />
              )
            ) : (
              <div className="w-full h-40 rounded-lg border border-[#C9A34A]/40 bg-white grid place-items-center text-[#14513D]/40"><FileText className="h-8 w-8" /></div>
            )}
          </div>
        </div>
        {p.report_url && <ReportLightbox src={fileUrl(p.report_url)} title={`${teaName} — tasting report`} open={lightbox} onOpenChange={setLightbox} />}
        <ShareSheet open={shareOpen} onOpenChange={setShareOpen} title={`${teaName} tasting sheet (JPEG)`} message={shareMsg} prepare={prepareShareJpg} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="view-close">Close</Button>
          <Button onClick={onEdit} data-testid="view-edit" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-1"><Pencil className="h-4 w-4" /> Edit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
